import { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Globe, Building2, Users, TrendingUp, Mail, Phone, MapPin, ChevronRight, ExternalLink, ArrowUp, Menu, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { translations } from "./translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

// Language Provider with memoized context value
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('origen-lang');
    if (saved) return saved;
    const browserLang = navigator.language.slice(0, 2);
    return browserLang === 'es' ? 'es' : 'en';
  });

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => {
      const newLang = prev === 'en' ? 'es' : 'en';
      localStorage.setItem('origen-lang', newLang);
      return newLang;
    });
  }, []);

  const t = translations[language];

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ 
    language, 
    toggleLanguage, 
    t 
  }), [language, toggleLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Language Toggle Button
const LanguageToggle = () => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all hover:scale-110"
      style={{ 
        background: 'linear-gradient(135deg, #f97316, #ea580c)',
        color: '#ffffff',
        boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
      }}
      data-testid="language-toggle"
      title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
    >
      <Globe className="w-4 h-4" />
      <span className="uppercase tracking-wide">{language === 'en' ? 'ES' : 'EN'}</span>
      <span className="text-xs opacity-80">|</span>
      <span className="text-xs">{language === 'en' ? 'Español' : 'English'}</span>
    </button>
  );
};

// Logo component
const OrigenLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_web-creator-1089/artifacts/s4fucz7t_ChatGPT%20Image%20Mar%2011%2C%202026%2C%2007_26_54%20PM.png" 
    alt="Origen Tech" 
    className="h-10 w-auto"
  />
);

// Navigation component
const Navigation = ({ activeSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t.nav.about },
    { href: '#vision', label: t.nav.vision },
    { href: '#products', label: t.nav.products },
    { href: '#investors', label: t.nav.investors },
    { href: '#contact', label: t.nav.contact },
  ];

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        backgroundColor: isScrolled ? 'rgba(10, 22, 40, 0.95)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(12px)' : 'none',
        boxShadow: isScrolled ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
      }}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3" data-testid="logo-link">
            <OrigenLogo />
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium transition-colors"
                style={{ color: activeSection === link.href.slice(1) ? '#22c55e' : 'rgba(255,255,255,0.8)' }}
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
            <LanguageToggle />
            <a
              href="#contact"
              className="px-5 py-2 rounded-full text-sm font-semibold hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(to right, #22c55e, #0ea5e9)', color: '#0a1628' }}
              data-testid="nav-cta-button"
            >
              {t.nav.getInTouch}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            style={{ color: '#ffffff' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 transition-colors"
                style={{ color: 'rgba(255,255,255,0.8)' }}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
            <div className="py-3">
              <LanguageToggle />
            </div>
            <a
              href="#contact"
              className="block mt-4 px-5 py-3 rounded-full text-sm font-semibold text-center"
              style={{ background: 'linear-gradient(to right, #22c55e, #0ea5e9)', color: '#0a1628' }}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-cta"
            >
              {t.nav.getInTouch}
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0" style={{ backgroundColor: '#0a1628' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, #17305c, #0a1628, #060d17)' }} />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-20">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ backgroundColor: 'rgba(34,197,94,0.3)' }} />
          <div className="absolute bottom-1/4 right-1/3 w-72 h-72 rounded-full blur-3xl animate-pulse-slow delay-1000" style={{ backgroundColor: 'rgba(14,165,233,0.2)' }} />
        </div>
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
        <div className="animate-fade-in-up">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#22c55e' }} />
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.hero.badge}</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ color: '#ffffff' }}>
            {t.hero.headline1}
            <br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, #22c55e, #0ea5e9, #22c55e)' }}>
              {t.hero.headline2}
            </span>
          </h1>
          
          <p className="text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {t.hero.subheadline}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#products"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all transform hover:scale-105"
              style={{ background: 'linear-gradient(to right, #22c55e, #0ea5e9)', color: '#0a1628' }}
              data-testid="hero-products-button"
            >
              {t.hero.ourProducts}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', color: '#ffffff' }}
              data-testid="hero-contact-button"
            >
              <Mail className="w-5 h-5" />
              {t.hero.contactUs}
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full flex justify-center pt-2" style={{ border: '2px solid rgba(255,255,255,0.2)' }}>
            <div className="w-1 h-3 rounded-full animate-scroll" style={{ backgroundColor: 'rgba(255,255,255,0.4)' }} />
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="about" className="py-24 relative" style={{ backgroundColor: '#060d17' }} data-testid="about-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #0a1628, #060d17)' }} />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in">
            <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: '#22c55e' }}>{t.about.label}</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6" style={{ color: '#ffffff' }}>
              {t.about.title}
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t.about.paragraph1}
            </p>
            <p className="text-lg leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t.about.paragraph2}
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Globe className="w-10 h-10 mb-4" style={{ color: '#22c55e' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#ffffff' }}>{t.about.crossBorder}</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{t.about.crossBorderDesc}</p>
              </div>
              <div className="rounded-2xl p-6" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Building2 className="w-10 h-10 mb-4" style={{ color: '#0ea5e9' }} />
                <h3 className="font-semibold mb-2" style={{ color: '#ffffff' }}>{t.about.realEstateFocus}</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{t.about.realEstateDesc}</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl blur-2xl" style={{ background: 'linear-gradient(to right, rgba(34,197,94,0.2), rgba(14,165,233,0.2))' }} />
            <img 
              src="https://images.unsplash.com/photo-1568632234163-6786c4f9f282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsYXRpbiUyMGFtZXJpY2ElMjBpbnZlc3RtZW50JTIwY2l0eXNjYXBlJTIwbW9kZXJufGVufDB8fHx8MTc3MzI3MTY2Nnww&ixlib=rb-4.1.0&q=85"
              alt="Latin America cityscape"
              className="relative rounded-2xl w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Vision Section
const VisionSection = () => {
  const { t } = useLanguage();
  
  // Use stable IDs instead of array indices
  const visionCards = [
    {
      id: 'accessible-investment',
      icon: <TrendingUp className="w-8 h-8" />,
      title: t.vision.accessibleTitle,
      description: t.vision.accessibleDesc
    },
    {
      id: 'market-connection',
      icon: <Users className="w-8 h-8" />,
      title: t.vision.connectionTitle,
      description: t.vision.connectionDesc
    },
    {
      id: 'transparent-platforms',
      icon: <Globe className="w-8 h-8" />,
      title: t.vision.transparentTitle,
      description: t.vision.transparentDesc
    }
  ];

  return (
    <section id="vision" className="py-24 relative overflow-hidden" style={{ backgroundColor: '#0a1628' }} data-testid="vision-section">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(34,197,94,0.05)' }} />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(14,165,233,0.05)' }} />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: '#22c55e' }}>{t.vision.label}</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6" style={{ color: '#ffffff' }}>
            {t.vision.title}
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {t.vision.description}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {visionCards.map((item) => (
            <div 
              key={item.id}
              className="group rounded-2xl p-8 transition-all duration-300"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="mb-6 group-hover:scale-110 transition-transform" style={{ color: '#22c55e' }}>
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#ffffff' }}>{item.title}</h3>
              <p className="leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// App Store Icons components
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

// App Store and Play Store URLs (constants)
const APP_STORE_URL = "https://apps.apple.com/ec/app/ecuahome/id6766123392";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.origennet.ecuahome.app";

// Helper function to detect user's operating system
const getUserOS = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  return 'desktop';
};

// Feature list item with stable ID
const PRODUCT_FEATURES = [
  { id: 'feature-discover', key: 'feature1' },
  { id: 'feature-connect', key: 'feature2' },
  { id: 'feature-showcase', key: 'feature3' },
  { id: 'feature-ecuador', key: 'feature4' }
];

// App Store Button Component
const AppStoreButton = ({ url, isHighlighted, t }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl transition-all hover:scale-105"
    style={{ 
      backgroundColor: isHighlighted ? '#ffffff' : 'rgba(255,255,255,0.1)',
      color: isHighlighted ? '#000000' : '#ffffff',
      border: '1px solid rgba(255,255,255,0.2)'
    }}
    data-testid="app-store-button"
  >
    <AppleIcon />
    <div className="text-left">
      <div className="text-xs opacity-70">{t.products.downloadAppStore}</div>
      <div className="font-semibold text-sm">{t.products.appStore}</div>
    </div>
  </a>
);

// Play Store Button Component
const PlayStoreButton = ({ url, isHighlighted, t }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl transition-all hover:scale-105"
    style={{ 
      backgroundColor: isHighlighted ? '#ffffff' : 'rgba(255,255,255,0.1)',
      color: isHighlighted ? '#000000' : '#ffffff',
      border: '1px solid rgba(255,255,255,0.2)'
    }}
    data-testid="play-store-button"
  >
    <PlayStoreIcon />
    <div className="text-left">
      <div className="text-xs opacity-70">{t.products.getItOn}</div>
      <div className="font-semibold text-sm">{t.products.googlePlay}</div>
    </div>
  </a>
);

// QR Code Section Component
const QRCodeSection = ({ t }) => (
  <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
    <p className="text-sm font-medium mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
      {t.products.scanPhone}
    </p>
    <div className="flex flex-wrap gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="p-3 rounded-xl" style={{ backgroundColor: '#ffffff' }}>
          <QRCodeSVG value={APP_STORE_URL} size={100} level="M" data-testid="app-store-qr" />
        </div>
        <span className="text-xs flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <AppleIcon /> iOS
        </span>
      </div>
      <div className="flex flex-col items-center gap-2">
        <div className="p-3 rounded-xl" style={{ backgroundColor: '#ffffff' }}>
          <QRCodeSVG value={PLAY_STORE_URL} size={100} level="M" data-testid="play-store-qr" />
        </div>
        <span className="text-xs flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <PlayStoreIcon /> Android
        </span>
      </div>
    </div>
  </div>
);

// Products Section - Ecuahome
const ProductsSection = () => {
  const { t } = useLanguage();
  const userOS = getUserOS();

  return (
    <section id="products" className="py-24 relative" style={{ backgroundColor: '#060d17' }} data-testid="products-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #0a1628, #060d17)' }} />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: '#22c55e' }}>{t.products.label}</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4" style={{ color: '#ffffff' }}>
            {t.products.title}
          </h2>
        </div>
        
        <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(to bottom right, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="grid lg:grid-cols-2 gap-0">
            <div className="p-10 lg:p-16 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 w-fit mb-6" style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#f97316' }} />
                <span className="text-sm font-medium" style={{ color: '#f97316' }}>{t.products.badge}</span>
              </div>
              
              <h3 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>
                {t.products.ecuahomeTitle}
              </h3>
              <p className="text-xl font-medium mb-6" style={{ color: '#22c55e' }}>
                {t.products.ecuahomeSubtitle}
              </p>
              <p className="text-lg leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {t.products.ecuahomeDesc}
              </p>
              
              <ul className="space-y-4 mb-8">
                {PRODUCT_FEATURES.map((feature) => (
                  <li key={feature.id} className="flex items-center gap-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.2)' }}>
                      <ChevronRight className="w-4 h-4" style={{ color: '#22c55e' }} />
                    </span>
                    {t.products[feature.key]}
                  </li>
                ))}
              </ul>
              
              {/* App Download Section */}
              <div className="mb-6">
                <p className="text-sm font-medium mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {t.products.downloadApp}
                </p>
                <div className="flex flex-wrap gap-3">
                  <AppStoreButton url={APP_STORE_URL} isHighlighted={userOS === 'ios'} t={t} />
                  <PlayStoreButton url={PLAY_STORE_URL} isHighlighted={userOS === 'android'} t={t} />
                </div>

                {userOS === 'desktop' && <QRCodeSection t={t} />}
              </div>

              {/* Website link */}
              <a
                href="https://ecuahome.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm font-medium transition-colors"
                style={{ color: 'rgba(255,255,255,0.6)' }}
                data-testid="ecuahome-website-link"
              >
                {t.products.orVisit}
                <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            
            <div className="relative h-[400px] lg:h-auto">
              <div className="absolute inset-0 z-10 lg:hidden" style={{ background: 'linear-gradient(to right, #060d17, transparent, transparent)' }} />
              <img 
                src="https://images.unsplash.com/photo-1749704647512-3f556575a241?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwcHJvcGVydHklMjBob21lfGVufDB8fHx8MTc3MzI3MTY2N3ww&ixlib=rb-4.1.0&q=85"
                alt="Modern real estate property"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,13,23,0.8), transparent)' }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Investors Section
const InvestorsSection = () => {
  const { t } = useLanguage();
  
  return (
    <section id="investors" className="py-24 relative overflow-hidden" style={{ backgroundColor: '#0a1628' }} data-testid="investors-section">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(34,197,94,0.05)' }} />
      </div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: '#22c55e' }}>{t.investors.label}</span>
        <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6" style={{ color: '#ffffff' }}>
          {t.investors.title}
        </h2>
        <p className="text-xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
          {t.investors.paragraph1}
        </p>
        <p className="text-lg leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {t.investors.paragraph2}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#contact"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all"
            style={{ background: 'linear-gradient(to right, #22c55e, #0ea5e9)', color: '#0a1628' }}
            data-testid="investor-contact-button"
          >
            <TrendingUp className="w-5 h-5" />
            {t.investors.partnerWithUs}
          </a>
          <a
            href="mailto:help@origennet.com?subject=Investment%20Inquiry"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', color: '#ffffff' }}
            data-testid="investor-email-button"
          >
            <Mail className="w-5 h-5" />
            {t.investors.emailUs}
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  return (
    <section id="contact" className="py-24 relative" style={{ backgroundColor: '#060d17' }} data-testid="contact-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #0a1628, #060d17)' }} />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold tracking-wider uppercase" style={{ color: '#22c55e' }}>{t.contact.label}</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4" style={{ color: '#ffffff' }}>
            {t.contact.title}
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {t.contact.description}
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 className="text-xl font-semibold mb-6" style={{ color: '#ffffff' }}>{t.contact.getInTouch}</h3>
              
              <div className="space-y-6">
                <a 
                  href="mailto:help@origennet.com" 
                  className="flex items-center gap-4 transition-colors group"
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                    <Mail className="w-6 h-6" style={{ color: '#22c55e' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.contact.email}</p>
                    <p style={{ color: '#ffffff' }}>help@origennet.com</p>
                  </div>
                </a>
                
                <a 
                  href="tel:+15016696777" 
                  className="flex items-center gap-4 transition-colors group"
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(14,165,233,0.1)' }}>
                    <Phone className="w-6 h-6" style={{ color: '#0ea5e9' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.contact.phone}</p>
                    <p style={{ color: '#ffffff' }}>+1 (501) 669-6777</p>
                  </div>
                </a>
                
                <a 
                  href="https://origennet.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 transition-colors group"
                  style={{ color: 'rgba(255,255,255,0.8)' }}
                  data-testid="contact-website-link"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors" style={{ backgroundColor: 'rgba(249,115,22,0.1)' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#f97316' }} />
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{t.contact.website}</p>
                    <p style={{ color: '#ffffff' }}>origennet.com</p>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Additional image */}
            <div className="relative rounded-2xl overflow-hidden h-[200px]">
              <img 
                src="https://images.unsplash.com/photo-1593985437133-03d5e1435c03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxsYXRpbiUyMGFtZXJpY2ElMjBpbnZlc3RtZW50JTIwY2l0eXNjYXBlJTIwbW9kZXJufGVufDB8fHx8MTc3MzI3MTY2Nnww&ixlib=rb-4.1.0&q=85"
                alt="Latin American cityscape"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,13,23,0.8), transparent)' }} />
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-xl font-semibold mb-6" style={{ color: '#ffffff' }}>{t.contact.sendMessage}</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.contact.nameLabel}</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full rounded-xl px-4 py-3 focus:outline-none transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' }}
                  placeholder={t.contact.namePlaceholder}
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.contact.emailLabel}</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full rounded-xl px-4 py-3 focus:outline-none transition-colors"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' }}
                  placeholder={t.contact.emailPlaceholder}
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.contact.messageLabel}</label>
                <textarea
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="w-full rounded-xl px-4 py-3 focus:outline-none transition-colors resize-none"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ffffff' }}
                  placeholder={t.contact.messagePlaceholder}
                  data-testid="contact-message-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(to right, #22c55e, #0ea5e9)', color: '#0a1628' }}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? t.contact.sending : t.contact.submitButton}
              </button>
              
              {submitStatus === 'success' && (
                <p className="text-center" style={{ color: '#22c55e' }} data-testid="contact-success-message">
                  {t.contact.successMessage}
                </p>
              )}
              
              {submitStatus === 'error' && (
                <p className="text-center" style={{ color: '#ef4444' }} data-testid="contact-error-message">
                  {t.contact.errorMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { href: '#about', label: t.nav.about },
    { href: '#vision', label: t.nav.vision },
    { href: '#products', label: t.nav.products },
    { href: '#investors', label: t.nav.investors },
    { href: '#contact', label: t.nav.contact },
  ];

  return (
    <footer className="py-12" style={{ backgroundColor: '#060d17', borderTop: '1px solid rgba(255,255,255,0.1)' }} data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <OrigenLogo />
            </div>
            <p className="text-sm leading-relaxed max-w-md" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t.footer.description}
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: '#ffffff' }}>{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href}
                    className="transition-colors text-sm hover:opacity-100"
                    style={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: '#ffffff' }}>{t.footer.contact}</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:help@origennet.com" 
                  className="transition-colors text-sm flex items-center gap-2"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  <Mail className="w-4 h-4" />
                  help@origennet.com
                </a>
              </li>
              <li>
                <a 
                  href="tel:+15016696777" 
                  className="transition-colors text-sm flex items-center gap-2"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  <Phone className="w-4 h-4" />
                  +1 (501) 669-6777
                </a>
              </li>
              <li>
                <a 
                  href="https://origennet.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="transition-colors text-sm flex items-center gap-2"
                  style={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  <Globe className="w-4 h-4" />
                  origennet.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {t.footer.copyright.replace('{year}', new Date().getFullYear())}
          </p>
          
          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 transition-colors text-sm"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            data-testid="back-to-top-button"
          >
            {t.footer.backToTop}
            <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <div className="App min-h-screen" style={{ backgroundColor: '#0a1628' }}>
      <Navigation activeSection={activeSection} />
      <HeroSection />
      <AboutSection />
      <VisionSection />
      <ProductsSection />
      <InvestorsSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

// Export App directly (LanguageProvider is now in index.js)
export default App;
